import "./Profile.scss";
import { useEffect, useMemo, useState } from "react";
import Header from "../../components/Header/Header";
import { useTheme, ThemeMode } from "../../hooks/useTheme";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useLogout } from "../../hooks/useLogout";
import { UserApi } from "../../services/api/user";
import { UserProfileUpdateRequest } from "../../types/types";
import { getApiErrorMessage } from "../../utils/api/getApiErrorMessage";

export const Profile = () => {
  const logout = useLogout();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { user, setUser, error: loadError } = useCurrentUser();

  const [error, setError] = useState<string | null>(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [editingUsername, setEditingUsername] = useState(false);
  const [savingUsername, setSavingUsername] = useState(false);
  const [username, setUsername] = useState("");
  const [usernameMessage, setUsernameMessage] = useState<string | null>(null);
  const [savingPassword, setSavingPassword] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [deletionRequestSending, setDeletionRequestSending] = useState(false);
  const [deletionRequestSent, setDeletionRequestSent] = useState(false);
  const [deletionConfirmationOpen, setDeletionConfirmationOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [form, setForm] = useState<UserProfileUpdateRequest>({
    fullname: "",
    mail: "",
    telegram: "",
    phone: "",
  });

  useEffect(() => {
    if (!user) {
      return;
    }

    setForm({
      fullname: user.fullname ?? "",
      mail: user.mail ?? "",
      telegram: user.telegram ?? "",
      phone: user.phone ?? "",
    });
    setUsername(user.username);
  }, [user]);

  useEffect(() => {
    if (loadError) {
      setError(getApiErrorMessage(loadError, "Ошибка загрузки профиля"));
    }
  }, [loadError]);

  const mailRegex = useMemo(() => /^$|^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/, []);
  const phoneRegex = useMemo(() => /^$|^\+?[0-9]{7,20}$/, []);

  const validationError = useMemo(() => {
    const mail = (form.mail ?? "").trim();
    const phone = (form.phone ?? "").trim();

    if (!mailRegex.test(mail)) return "Неверный формат email";
    if (!phoneRegex.test(phone)) return "Неверный формат телефона";
    return null;
  }, [form.mail, form.phone, mailRegex, phoneRegex]);

  const usernameValidationError = useMemo(() => {
    if (!editingUsername) return null;
    const next = username.trim();
    if (!next) return "Username не может быть пустым";
    if (next.length < 4 || next.length > 30) return "Username должен быть от 4 до 30 символов";
    return null;
  }, [editingUsername, username]);

  const passwordValidationError = useMemo(() => {
    if (!editingPassword) return null;
    if (!currentPassword && !newPassword && !confirmPassword) return null;
    if (!currentPassword.trim()) return "Введите текущий пароль";
    if (newPassword.length < 8 || newPassword.length > 30) return "Новый пароль должен быть от 8 до 30 символов";
    if (newPassword !== confirmPassword) return "Пароли не совпадают";
    return null;
  }, [confirmPassword, currentPassword, editingPassword, newPassword]);

  const clearMessages = () => {
    setError(null);
    setProfileSaved(false);
    setUsernameMessage(null);
    setPasswordMessage(null);
  };

  const onSaveProfile = async () => {
    clearMessages();

    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSavingProfile(true);
      const updated = await UserApi.updateMe(form);
      setUser(updated);
      setProfileSaved(true);
      setEditingProfile(false);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Ошибка сохранения"));
    } finally {
      setSavingProfile(false);
    }
  };

  const onCancelProfile = () => {
    clearMessages();
    setEditingProfile(false);
    setForm({
      fullname: user?.fullname ?? "",
      mail: user?.mail ?? "",
      telegram: user?.telegram ?? "",
      phone: user?.phone ?? "",
    });
  };

  const onSaveUsername = async () => {
    clearMessages();

    if (usernameValidationError) {
      setError(usernameValidationError);
      return;
    }

    if (!user) {
      return;
    }

    const next = username.trim();
    if (next === user.username) {
      setEditingUsername(false);
      return;
    }

    try {
      setSavingUsername(true);
      await UserApi.updateUsername({ username: next });
      setUsernameMessage("Имя пользователя изменено. Нужно войти заново.");
      logout();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Ошибка изменения username"));
    } finally {
      setSavingUsername(false);
    }
  };

  const onCancelUsername = () => {
    clearMessages();
    setEditingUsername(false);
    setUsername(user?.username ?? "");
  };

  const onSavePassword = async () => {
    clearMessages();

    if (passwordValidationError) {
      setError(passwordValidationError);
      return;
    }

    try {
      setSavingPassword(true);
      await UserApi.changePassword({
        currentPassword,
        newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordMessage("Пароль изменен");
      setEditingPassword(false);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Ошибка смены пароля"));
    } finally {
      setSavingPassword(false);
    }
  };

  const onCancelPassword = () => {
    clearMessages();
    setEditingPassword(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const onRequestAccountDeletion = async () => {
    setError(null);
    setDeletionRequestSent(false);
    setDeletionRequestSending(true);
    try {
      await UserApi.requestAccountDeletion();
      setDeletionRequestSent(true);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Не удалось отправить запрос на удаление аккаунта"));
    } finally {
      setDeletionRequestSending(false);
    }
  };

  const themeOptions: Array<{ value: ThemeMode; label: string; hint: string }> = [
    {
      value: "system",
      label: "Как в системе",
      hint: `Сейчас применяется ${resolvedTheme === "dark" ? "темная" : "светлая"} тема`,
    },
    { value: "light", label: "Светлая", hint: "Всегда использовать светлую тему" },
    { value: "dark", label: "Темная", hint: "Всегда использовать темную тему" },
  ];

  return (
    <>
      <Header />
      <main className="profile">
        <div className="profile__card">
          <h2 className="profile__title">Профиль</h2>

          {!user && !error ? <p className="profile__message">Загрузка…</p> : null}
          {error ? <p className="profile__error">{error}</p> : null}
          {profileSaved ? <p className="profile__message">Сохранено</p> : null}
          {usernameMessage ? <p className="profile__message">{usernameMessage}</p> : null}
          {passwordMessage ? <p className="profile__message">{passwordMessage}</p> : null}
          {deletionRequestSent ? <p className="profile__message">Запрос отправлен. Мы рассмотрим его.</p> : null}

          {user ? (
            <>
              <div className="profile__meta">
                <span>Роль: {user.role}</span>
              </div>

              <div className="profile__section">
                <h3 className="profile__section-title">Тема оформления</h3>
                <div className="profile__themeList" role="radiogroup" aria-label="Выбор темы">
                  {themeOptions.map((option) => (
                    <label key={option.value} className="profile__themeOption">
                      <input
                        className="profile__themeRadio"
                        type="radio"
                        name="theme-mode"
                        value={option.value}
                        checked={theme === option.value}
                        onChange={() => setTheme(option.value)}
                      />
                      <span className="profile__themeContent">
                        <span className="profile__themeLabel">{option.label}</span>
                        <span className="profile__themeHint">{option.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="profile__section">
                <h3 className="profile__section-title">Данные пользователя</h3>

                {!editingProfile ? (
                  <>
                    <div className="profile__subscriptionBadge">
                      <span className="profile__subscriptionValue">{user.subscriptionName ?? "Без подписки"}</span>
                    </div>

                    <div className="profile__row">
                      <div className="profile__label">Username</div>
                      <div className="profile__value">{user.username}</div>
                    </div>
                    <div className="profile__row">
                      <div className="profile__label">ФИО</div>
                      <div className="profile__value">{user.fullname ?? ""}</div>
                    </div>
                    <div className="profile__row">
                      <div className="profile__label">Email</div>
                      <div className="profile__value">{user.mail ?? ""}</div>
                    </div>
                    <div className="profile__row">
                      <div className="profile__label">Telegram</div>
                      <div className="profile__value">{user.telegram ?? ""}</div>
                    </div>
                    <div className="profile__row">
                      <div className="profile__label">Телефон</div>
                      <div className="profile__value">{user.phone ?? ""}</div>
                    </div>

                    <div className="profile__actions">
                      <button className="profile__button profile__buttonPrimary" onClick={() => setEditingProfile(true)}>
                        Редактировать
                      </button>
                      <button className="profile__button" onClick={() => setEditingUsername(true)}>
                        Редактировать username
                      </button>
                      <button className="profile__button" onClick={() => setEditingPassword(true)}>
                        Сменить пароль
                      </button>
                      <button className="profile__button profile__buttonDanger" onClick={logout}>
                        Выйти
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="profile__form">
                    <label>
                      ФИО
                      <input
                        className="profile__input"
                        value={form.fullname ?? ""}
                        onChange={(e) => setForm((prev) => ({ ...prev, fullname: e.target.value }))}
                      />
                    </label>

                    <label>
                      Email
                      <input
                        className="profile__input"
                        value={form.mail ?? ""}
                        onChange={(e) => setForm((prev) => ({ ...prev, mail: e.target.value }))}
                      />
                    </label>

                    <label>
                      Telegram
                      <input
                        className="profile__input"
                        value={form.telegram ?? ""}
                        onChange={(e) => setForm((prev) => ({ ...prev, telegram: e.target.value }))}
                      />
                    </label>

                    <label>
                      Телефон
                      <input
                        className="profile__input"
                        value={form.phone ?? ""}
                        onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                      />
                    </label>

                    <div className="profile__actions">
                      <button
                        className="profile__button profile__buttonPrimary"
                        onClick={onSaveProfile}
                        disabled={savingProfile}
                      >
                        {savingProfile ? "Сохранение..." : "Сохранить"}
                      </button>
                      <button className="profile__button" onClick={onCancelProfile} disabled={savingProfile}>
                        Отмена
                      </button>
                      <button className="profile__button profile__buttonDanger" onClick={logout} disabled={savingProfile}>
                        Выйти
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="profile__section">
                <h3 className="profile__section-title">Удаление аккаунта</h3>
                <p className="profile__message">
                  Запрос будет отправлен в службу поддержки с данными, необходимыми для поиска аккаунта. Пароль и
                  токены доступа не передаются. Ответ по обращению направим в течение 10 рабочих дней.
                </p>
                <div className="profile__actions">
                  <button
                    className="profile__button profile__buttonDanger"
                    type="button"
                    onClick={() => setDeletionConfirmationOpen(true)}
                    disabled={deletionRequestSending}
                  >
                    {deletionRequestSending ? "Отправляем..." : "Запросить удаление аккаунта"}
                  </button>
                </div>
              </div>

              {deletionConfirmationOpen ? (
                <div className="profile__confirmBackdrop" role="presentation">
                  <section className="profile__confirmDialog" role="dialog" aria-modal="true" aria-labelledby="deletion-confirm-title">
                    <h3 id="deletion-confirm-title">Подтвердить запрос</h3>
                    <p>Отправить запрос на удаление аккаунта и связанных с ним данных?</p>
                    <div className="profile__actions">
                      <button className="profile__button" type="button" onClick={() => setDeletionConfirmationOpen(false)}>
                        Отмена
                      </button>
                      <button
                        className="profile__button profile__buttonDanger"
                        type="button"
                        disabled={deletionRequestSending}
                        onClick={() => {
                          setDeletionConfirmationOpen(false);
                          void onRequestAccountDeletion();
                        }}
                      >
                        Отправить запрос
                      </button>
                    </div>
                  </section>
                </div>
              ) : null}

              {editingUsername ? (
                <div className="profile__section">
                  <div className="profile__form">
                    <label>
                      Username
                      <input className="profile__input" value={username} onChange={(e) => setUsername(e.target.value)} />
                    </label>
                    <div className="profile__actions">
                      <button
                        className="profile__button profile__buttonPrimary"
                        onClick={onSaveUsername}
                        disabled={savingUsername}
                      >
                        {savingUsername ? "Сохранение..." : "Сохранить username"}
                      </button>
                      <button className="profile__button" onClick={onCancelUsername} disabled={savingUsername}>
                        Отмена
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {editingPassword ? (
                <div className="profile__section">
                  <div className="profile__form">
                    <label>
                      Текущий пароль
                      <input
                        className="profile__input"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                      />
                    </label>
                    <label>
                      Новый пароль
                      <input
                        className="profile__input"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                    </label>
                    <label>
                      Повторите новый пароль
                      <input
                        className="profile__input"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                    </label>

                    <div className="profile__actions">
                      <button
                        className="profile__button profile__buttonPrimary"
                        onClick={onSavePassword}
                        disabled={savingPassword}
                      >
                        {savingPassword ? "Сохранение..." : "Изменить пароль"}
                      </button>
                      <button className="profile__button" onClick={onCancelPassword} disabled={savingPassword}>
                        Отмена
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </main>
    </>
  );
};
